'use client';

import { useLocale, useTranslations } from 'next-intl';
import { Sparkles } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/components/ui/popover';
import { useAllowanceMeter } from '../hooks/useAllowanceMeter';
import { openSubscription } from '../lib/accountsTopUp';

/**
 * AllowanceMeter — the HIDDEN-TOKENS header chip (subscription-first model).
 *
 * The wallet is centralized in accounts, but the UI NEVER shows a raw token
 * count. Instead getAllowanceMeter() turns the per-app allowance into a % (of
 * the period grant) plus a few content-unit estimates ("≈ N illustrated stories
 * / voiceovers left"). We render:
 *   • hasAllowance === false → a subtle "Subscribe" link (no meter).
 *   • otherwise             → a compact % ring + label, click-expands to the
 *                              estimates + refill date + "Manage subscription".
 *
 * Null (anonymous / legacy TokenManager) → renders nothing while the allowance
 * is unknown. The number itself comes from {@link useAllowanceMeter}, which is
 * shared with every other component showing it and refreshes on the wallet
 * event and on focus.
 */
export default function AllowanceMeter() {
  const t = useTranslations('subscription.allowance');
  const locale = useLocale();
  const { data } = useAllowanceMeter();

  if (!data) return null; // anonymous / legacy / still loading

  // No subscription and no live bonus → a compact, fixed-width subscribe affordance
  // (icon only, so it never widens the header row and pushes the profile name out).
  if (!data.hasAllowance) {
    return (
      <button
        type="button"
        onClick={() => openSubscription(locale)}
        data-testid="allowance-subscribe"
        aria-label={t('subscribe')}
        title={t('subscribe')}
        className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border text-primary hover:bg-muted transition-colors focus:outline-none focus:ring-2 focus:ring-primary"
      >
        <Sparkles className="size-4" aria-hidden />
      </button>
    );
  }

  // FLOOR, not round: as soon as ANY allowance is spent the meter must tick below
  // 100% (round kept showing 100% until 0.5% was gone — invisible against a big
  // grant). 100% now means truly nothing spent; any spend reads 99% or lower.
  const raw = Math.max(0, Math.min(1, data.percent)) * 100;
  const pct = raw >= 100 ? 100 : Math.max(raw > 0 ? 1 : 0, Math.floor(raw));

  const parts: string[] = [];
  if (data.estimates.smallBook != null)
    parts.push(t('smallBooks', { count: data.estimates.smallBook }));
  if (data.estimates.voiceover != null)
    parts.push(t('voiceovers', { count: data.estimates.voiceover }));

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="allowance-meter"
          aria-label={`${t('percentLabel')}: ${pct}%`}
          title={t('percentLabel')}
          className="inline-flex h-9 shrink-0 items-center gap-1 rounded-lg border border-border px-2 text-sm hover:bg-muted transition-colors focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <svg viewBox="0 0 36 36" className="size-5 shrink-0" aria-hidden="true">
            <circle
              cx="18"
              cy="18"
              r="15.9155"
              fill="none"
              stroke="currentColor"
              strokeWidth="4"
              className="text-muted-foreground/25"
            />
            <circle
              cx="18"
              cy="18"
              r="15.9155"
              fill="none"
              stroke="currentColor"
              strokeWidth="4"
              strokeLinecap="round"
              pathLength={100}
              strokeDasharray={`${pct} 100`}
              transform="rotate(-90 18 18)"
              className="text-primary"
            />
          </svg>
          <span className="font-medium tabular-nums">{pct}%</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 text-sm">
        <div className="space-y-1.5">
          {parts.length > 0 ? (
            <p>
              ≈ {parts.join(' · ')} {t('left')}
            </p>
          ) : null}
          {data.nextExpiry ? (
            <p className="text-muted-foreground">
              {data.hasSubscription
                ? t('refills', { when: relativeRefill(data.nextExpiry, locale) })
                : t('bonusExpires', { when: relativeRefill(data.nextExpiry, locale) })}
            </p>
          ) : null}
          <button
            type="button"
            onClick={() => openSubscription(locale)}
            data-testid="allowance-manage"
            className="block pt-1 font-medium text-primary hover:underline"
          >
            {data.hasSubscription ? t('manageSubscription') : t('subscribe')}
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

/** Locale-aware "in N days / hours" for the refill date (no raw timestamps). */
function relativeRefill(iso: string, locale: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const now = Date.now();
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  const days = Math.round((then - now) / 86_400_000);
  if (Math.abs(days) >= 1) return rtf.format(days, 'day');
  const hours = Math.round((then - now) / 3_600_000);
  return rtf.format(hours, 'hour');
}
